import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
  import Search from '@/components/Search.vue'
  import UserArea from '@/components/UserArea.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import CookieHelper from '@/helpers/CookieHelper'
  import { TOURS_AND_HINTS } from '@/helpers/Constants'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useIcons from '@/composables/useIcons'
  import useToursAndHints from '@/composables/useToursAndHints'
  import { NewsModule } from '@/store/modules/News'
  import ToggleSwitch from '@/components/common/ToggleSwitch.vue'
  import { EditorModule } from '@/store/modules/editor'
  import useEditor from '@/composables/useEditor'
  import { CourseModule } from '@/store/modules/course'
  import { UserModule } from '@/store/modules/user'
  import TrainingsMenuDropdown from '@/components/TrainingsMenuDropdown.vue'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useCourse from '@/composables/useCourse'
  import { useRoute } from 'vue-router/composables'
  import UserReferralCode from '@/components/common/UserReferralCode.vue'
  import eventBus from '@/main'
  import useI18n from '@/composables/useI18n'
  import useShopifyClient from '@/composables/useShopifyClient'

  export interface NavOption {
    name: string
    navEntity: NavEntity
    path: string
    show: boolean
    dataCy: string
  }

  export type NavEntity = 'trainings' | 'news' | 'library'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'KetchUpHeader',
  props: {
    logoUrl: {
      type: String,
      required: true,
    },
  },
  emits: ['hide-widgets', 'show-widgets'],
  setup(__props, { emit }) {

  

  

  const route = useRoute()

  const { isMobileDevice, isDesktopDevice, isLargeDesktop } = useBreakpoint()
  const { logo, allBookmarks, arrowDown, cardEmptyImage } = useIcons()
  const { showOrHideToursAndHints, hideAllHints } = useToursAndHints()
  const { inEditorMode, onEditorPages } = useEditor(route)
  const { showCompanyOnboardingPopupDetails, isSachkundeCompany, isKnowledgeCheckCompany } = useCommonMixin()
  const { hasPurchasedAtLeastACourse, course, trackCoursePurchaseIntended, productPrice } = useCourse()
  const { translateString } = useI18n()
  const { createShopifyCheckout } = useShopifyClient()

  const ancestorFolder = ref('')
  const openFolder = ref(false)
  const openSidePanel = ref(false)
  const checkedButton = ref(false)
  const panelAnimation = ref(false)
  const hoveringBookmark = ref(false)
  const hoveringTrainings = ref(false)
  const createSpaceForSearchInput = ref(false)
  const enableToursAndHints = ref(false)

  const showNewsItem = computed(() => UserModule.currentCompany?.showNewsSection ?? false)

  const showLibraryItem = computed(() => UserModule.currentCompany?.showLibrarySection ?? false)

  const navOptions = computed(
    () =>
      [
        {
          name: translateString('navigation.trainings'),
          navEntity: 'trainings',
          path: '/trainings',
          show: isDesktopDevice.value,
          dataCy: 'trainings',
        },
        {
          name: translateString('navigation.news'),
          navEntity: 'news',
          path: '/news',
          show: !isExtension.value && showNewsItem.value && hasPurchasedAtLeastACourse.value,
          dataCy: 'news-nav',
        },
        {
          name: translateString('navigation.library'),
          navEntity: 'library',
          path: '/library',
          show: showLibraryNavItem.value,
          dataCy: 'library',
        },
      ] as NavOption[],
  )

  const filteredNavOptions = computed(() => navOptions.value.filter((option) => option.show))

  const showLibraryNavItem = computed(() => {
    if (!eventBus?.$auth.isAuthenticated) return false
    return !isExtension.value && showLibraryItem.value && hasPurchasedAtLeastACourse.value
  })

  const routePath = computed(() => {
    return route.path
  })

  const toursAndHintsEnabled = computed(() => {
    if (enableToursAndHints.value) return true
    const cookie = CookieHelper.getCookieValue(TOURS_AND_HINTS)
    return !!cookie
  })

  const isEditable = computed(() => {
    return (
      (route.name === 'Trainings' && CourseModule.courseSections?.some((s) => s.isEditable)) ||
      !!(onEditorPages.value && CourseModule.course?.isEditable)
    )
  })

  const showReTriggerOnboardingPopupCTA = computed(() => UserModule.reTriggerOnboardingPopup)

  const newNewsContentLength = computed(() => {
    return NewsModule.newNewsContentLength
  })

  const userReferralCode = computed(() => UserModule.userReferralCode)

  const showBuyCoursePreview = computed(
    () =>
      isMobileDevice.value &&
      route.matched.some((route) => ['CourseDetails', 'Lesson', 'CourseModule'].includes(route.name!)) &&
      course.value?.trial &&
      course.value?.shopifyProductId &&
      !course.value?.isIddCourse,
  )

  const isExtension = computed(() => route.name === 'ChromeTabExtension')

  const toggleEditMode = (value: boolean) => {
    EditorModule.setEditMode(value)
    CookieHelper.setCrossDomainCookie('EDITOR_MODE', value.toString())
  }

  const toggleToursAndHints = (enable: boolean) => {
    enableToursAndHints.value = enable
    CookieHelper.setCookieValue(TOURS_AND_HINTS, enable.toString())
    if (enable) {
      if (isDesktopDevice.value) showOrHideToursAndHints(4000)
    } else {
      hideAllHints()
    }
  }

  const onRouteUpdate = (path: string) => {
    if (['Trainings', 'Library', 'AllNews'].includes(route.name as string)) {
      openFolder.value = false
      closeSidePanel()
      ancestorFolder.value = path.split('/')[1] as string
    } else {
      ancestorFolder.value = ''
    }
  }

  const closeSidePanel = () => {
    checkedButton.value = false
    openSidePanel.value = false
    if (document.documentElement.scrollTop > 0) {
      const wrapper = document.querySelector('#app > .header-wrapper')
      wrapper?.classList.add('apply-drop-shadow')
    }
    setTimeout(() => {
      panelAnimation.value = false
    }, 300)
  }

  const activateHelp = () => {
    eventBus?.$on('set-tours-and-hints', () => {
      CookieHelper.setCookieValue(TOURS_AND_HINTS, 'true')
      enableToursAndHints.value = true
    })
  }

  const toggleHamburger = () => {
    openSidePanel.value = !openSidePanel.value
    const wrapper = document.querySelector('#app > .header-wrapper')

    if (!openSidePanel.value) {
      if (document.documentElement.scrollTop > 0) {
        wrapper?.classList.add('apply-drop-shadow')
      }
      setTimeout(() => {
        panelAnimation.value = false
      }, 300)
    } else {
      if (wrapper?.classList.contains('apply-drop-shadow')) {
        wrapper.classList.remove('apply-drop-shadow')
      }
      panelAnimation.value = true
    }
  }

  const navItemCTA = (item: NavOption) => {
    if (item.navEntity === 'trainings' && isMobileDevice.value) {
      eventBus.$emit('show-modal', {
        modalContentComponent: 'TrainingsMenuDropdownModal',
        modalCloseCallback: (callback: () => void) => {
          if (typeof callback === 'function') callback()
        },
      })
      closeSidePanel()
    }
  }

  const shopifyCheckout = async () => {
    trackCoursePurchaseIntended()
    closeSidePanel()
    eventBus.$emit('turn-on-loader')
    await createShopifyCheckout()
    setTimeout(() => {
      eventBus.$emit('turn-off-loader')
    }, 2500)
  }

  const navItemMouseOver = (navEntity: string) => {
    if (navEntity === 'trainings' && !isMobileDevice.value) {
      hoveringTrainings.value = true
    }
  }

  watch(routePath, (value) => {
    onRouteUpdate(value)
  })

  watch(openSidePanel, (value) => {
    if (value) {
      emit('hide-widgets')
    } else {
      emit('show-widgets')
    }
  })

  onMounted(() => {
    onRouteUpdate(route.path)
    activateHelp()
  })

return { __sfc: true,emit, route, isMobileDevice, isDesktopDevice, isLargeDesktop, logo, allBookmarks, arrowDown, cardEmptyImage, showOrHideToursAndHints, hideAllHints, inEditorMode, onEditorPages, showCompanyOnboardingPopupDetails, isSachkundeCompany, isKnowledgeCheckCompany, hasPurchasedAtLeastACourse, course, trackCoursePurchaseIntended, productPrice, translateString, createShopifyCheckout, ancestorFolder, openFolder, openSidePanel, checkedButton, panelAnimation, hoveringBookmark, hoveringTrainings, createSpaceForSearchInput, enableToursAndHints, showNewsItem, showLibraryItem, navOptions, filteredNavOptions, showLibraryNavItem, routePath, toursAndHintsEnabled, isEditable, showReTriggerOnboardingPopupCTA, newNewsContentLength, userReferralCode, showBuyCoursePreview, isExtension, toggleEditMode, toggleToursAndHints, onRouteUpdate, closeSidePanel, activateHelp, toggleHamburger, navItemCTA, shopifyCheckout, navItemMouseOver, computed, onMounted, ref, watch, Search, UserArea, SVGRenderer, CookieHelper, TOURS_AND_HINTS, useBreakpoint, useIcons, useToursAndHints, NewsModule, ToggleSwitch, EditorModule, useEditor, CourseModule, UserModule, TrainingsMenuDropdown, useCommonMixin, useCourse, useRoute, UserReferralCode, eventBus, useI18n, useShopifyClient }
}

})